import { useEditCampaignSipTrunk } from '@features/Admin/Campaign/hooks/useEditCampaignSipTrunk';
import { Box, Button, Typography } from '@mui/material';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { SipSettingsForm } from '@shared/components/forms/SipSettingsForm';
import {
  CampaignDto,
  CampaignSipTrunkCreateRequestDto,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
} from '@shared/services/apiService';
import { SipTruncFieldsError } from '@shared/types/admin/campaign';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { mapCampaignSipToDefaultSip } from '../utils';
import { IconPhoneCheck } from '@tabler/icons-react';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';

interface SipTrunkTabProps {
  campaign: CampaignDto;
}

const SipTrunkTab: React.FC<SipTrunkTabProps> = ({ campaign }: SipTrunkTabProps) => {
  const currentUserRole = useAppSelector((state: RootState) => state.auth.authenticatedUser?.role);

  const [isSipValid, setSipValid] = useState(true);
  const [actualSipTrunkSetting, setActualSipTrunkSetting] =
    useState<CampaignSipTrunkCreateRequestDto | null>(null);
  const [sipTrunkCreateFieldError, setSipTrunkCreateFieldError] = useState<SipTruncFieldsError>({
    type: [],
    authUsername: [],
    authPassword: [],
    numbers: [],
    address: [],
    transport: [],
    editErrorMessage: '',
  });

  const { data: campaignSipTrunk, refetch: refetchCampaignSip } =
    useCampaignSipTrunkControllerGetSipTrunkQuery({
      id: campaign.id,
    });

  const notify = () => {
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  };

  const { onSubmit, formMethods } = useEditCampaignSipTrunk({
    setSipTrunkCreateFieldError,
    onSuccess: notify,
  });
  const { reset, setValue } = formMethods;

  const handleSipSettingsChange = (settings: CampaignSipTrunkCreateRequestDto | null) => {
    setActualSipTrunkSetting(settings);

    setValue('type', settings?.type ?? campaign.type);
    setValue('numbers', settings?.numbers ?? []);
    setValue('address', settings?.address ?? undefined);
    setValue('transport', settings?.transport);
    setValue('allowedAddresses', settings?.allowedAddresses ?? undefined);
    setValue('authUsername', settings?.authUsername ?? undefined);
    setValue('authPassword', settings?.authPassword ?? undefined);
  };

  const submitSipTrunk = async () => {
    try {
      await onSubmit();
      refetchCampaignSip();
    } catch (error) {
      console.error('Failed submitting sip trunk:', error);
    }
  };

  useEffect(() => {
    setValue('campaignId', campaign.id);
  }, [campaign.id]);

  useEffect(() => {
    if (campaignSipTrunk) {
      setActualSipTrunkSetting(mapCampaignSipToDefaultSip(campaignSipTrunk));
    } else {
      {
        setActualSipTrunkSetting(null);
      }
    }
  }, [campaignSipTrunk, sipTrunkCreateFieldError]);

  useEffect(() => {
    if (actualSipTrunkSetting) {
      reset({
        campaignId: campaign.id,
        address: actualSipTrunkSetting?.address || undefined,
        authPassword: actualSipTrunkSetting?.authPassword || undefined,
        authUsername: actualSipTrunkSetting?.authUsername || undefined,
        numbers: actualSipTrunkSetting?.numbers,
        transport: actualSipTrunkSetting?.transport || undefined,
        allowedAddresses: actualSipTrunkSetting?.allowedAddresses || undefined,
        type: campaign.type,
      });
    }
  }, [actualSipTrunkSetting]);

  return (
    <Box
      sx={{
        padding: '30px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
        }}>
        <IconPhoneCheck />
        <Typography fontSize="18px" fontWeight={600}>
          {t('campaignPage.editCampaign.sipTrunk')}
        </Typography>
      </Box>
      <Box sx={{ maxWidth: '600px', display: 'flex', flexDirection: 'column' }}>
        <SipSettingsForm
          settings={actualSipTrunkSetting}
          onChange={handleSipSettingsChange}
          onValidationChange={setSipValid}
          type={campaign.type}
          disabled={currentUserRole === UserRolesEnum.VIEWER}
        />

        <Button
          onClick={submitSipTrunk}
          variant="contained"
          color="primary"
          sx={{ alignSelf: 'center', width: '80%', maxWidth: '600px' }}
          disabled={!isSipValid || currentUserRole === UserRolesEnum.VIEWER}>
          {t('action.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default SipTrunkTab;
